// PouchDB revision tree utilities — extracted verbatim from pouchdb-browser/lib/index.es.js
// These are internal PouchDB functions that are NOT exported. We copy them here
// to avoid reimplementing the intricate revision tree logic (which broke replication
// in the first implementation attempt).

// ─── Rev tree types ───────────────────────────────────────────────────
// rev_tree is an array of: { pos: number, ids: TreeNode }
// TreeNode = [revHash: string, opts: { status?: string, deleted?: boolean }, children: TreeNode[]]

export function winningRev(metadata: any): string {
  let winningId: string | undefined;
  let winningPos: number | undefined;
  let winningDeleted: boolean | undefined;
  const toVisit = metadata.rev_tree.slice();
  let node: any;
  while ((node = toVisit.pop())) {
    const tree = node.ids;
    const branches = tree[2];
    const pos = node.pos;
    if (branches.length) {
      for (let i = 0, len = branches.length; i < len; i++) {
        toVisit.push({ pos: pos + 1, ids: branches[i] });
      }
      continue;
    }
    const deleted = !!tree[1].deleted;
    const id = tree[0];
    if (!winningId || (winningDeleted !== deleted ? winningDeleted :
        winningPos !== pos ? winningPos! < pos : winningId < id)) {
      winningId = id;
      winningPos = pos;
      winningDeleted = deleted;
    }
  }
  return winningPos + '-' + winningId;
}

export function traverseRevTree(revs: any[], callback: (isLeaf: boolean, pos: number, revHash: string, ctx: any, opts: any) => any): void {
  const toVisit = revs.slice();
  let node: any;
  while ((node = toVisit.pop())) {
    const pos = node.pos;
    const tree = node.ids;
    const branches = tree[2];
    const newCtx = callback(branches.length === 0, pos, tree[0], node.ctx, tree[1]);
    for (let i = 0, len = branches.length; i < len; i++) {
      toVisit.push({ pos: pos + 1, ids: branches[i], ctx: newCtx });
    }
  }
}

function sortByPos(a: any, b: any) {
  return a.pos - b.pos;
}

export function collectLeaves(revs: any[]): any[] {
  const leaves: any[] = [];
  traverseRevTree(revs, function (isLeaf, pos, id, _acc, opts) {
    if (isLeaf) {
      leaves.push({ rev: pos + '-' + id, pos, opts });
    }
  });
  leaves.sort(sortByPos).reverse();
  for (let i = 0, len = leaves.length; i < len; i++) {
    delete leaves[i].pos;
  }
  return leaves;
}

export function collectConflicts(metadata: any): string[] {
  const win = winningRev(metadata);
  const leaves = collectLeaves(metadata.rev_tree);
  const conflicts: string[] = [];
  for (let i = 0, len = leaves.length; i < len; i++) {
    const leaf = leaves[i];
    if (leaf.rev !== win && !leaf.opts.deleted) {
      conflicts.push(leaf.rev);
    }
  }
  return conflicts;
}

export function compactTree(metadata: any): string[] {
  const revs: string[] = [];
  traverseRevTree(metadata.rev_tree, function (isLeaf, pos, revHash, _ctx, opts) {
    if (opts.status === 'available' && !isLeaf) {
      revs.push(pos + '-' + revHash);
      opts.status = 'missing';
    }
  });
  return revs;
}

export function isDeleted(metadata: any, rev?: string): boolean {
  if (!rev) {
    rev = winningRev(metadata);
  }
  const id = rev.substring(rev.indexOf('-') + 1);
  let toVisit = metadata.rev_tree.map((n: any) => n.ids);
  let tree: any;
  while ((tree = toVisit.pop())) {
    if (tree[0] === id) {
      return !!tree[1].deleted;
    }
    toVisit = toVisit.concat(tree[2]);
  }
  return false;
}

export function isLocalId(id: string): boolean {
  return typeof id === 'string' && id.startsWith('_local/');
}

export function revExists(revs: any[], rev: string): boolean {
  const toVisit = revs.slice();
  const splitRev = rev.split('-');
  const targetPos = parseInt(splitRev[0], 10);
  const targetId = splitRev[1];
  let node: any;
  while ((node = toVisit.pop())) {
    if (node.pos === targetPos && node.ids[0] === targetId) {
      return true;
    }
    const branches = node.ids[2];
    for (let i = 0, len = branches.length; i < len; i++) {
      toVisit.push({ pos: node.pos + 1, ids: branches[i] });
    }
  }
  return false;
}

export function latest(rev: string, metadata: any): string {
  const toVisit = metadata.rev_tree.slice();
  let node: any;
  while ((node = toVisit.pop())) {
    const pos = node.pos;
    const tree = node.ids;
    const id = tree[0];
    const branches = tree[2];
    const isLeaf = branches.length === 0;
    const history = node.history ? node.history.slice() : [];
    history.push({ id, pos, opts: tree[1] });
    if (isLeaf) {
      for (let i = 0, len = history.length; i < len; i++) {
        const historyNode = history[i];
        const historyRev = historyNode.pos + '-' + historyNode.id;
        if (historyRev === rev) {
          return pos + '-' + id;
        }
      }
    }
    for (let j = 0, l = branches.length; j < l; j++) {
      toVisit.push({ pos: pos + 1, ids: branches[j], history });
    }
  }
  throw new Error('Unable to find leaf for revision ' + rev);
}

// ─── Merge utilities ──────────────────────────────────────────────────

function rootToLeaf(revs: any[]): any[] {
  const paths: any[] = [];
  const toVisit = revs.slice();
  let node: any;
  while ((node = toVisit.pop())) {
    const pos = node.pos;
    const tree = node.ids;
    const id = tree[0];
    const opts = tree[1];
    const branches = tree[2];
    const isLeaf = branches.length === 0;
    const history = node.history ? node.history.slice() : [];
    history.push({ id, opts });
    if (isLeaf) {
      paths.push({ pos: (pos + 1 - history.length), ids: history });
    }
    for (let i = 0, len = branches.length; i < len; i++) {
      toVisit.push({ pos: pos + 1, ids: branches[i], history });
    }
  }
  return paths.reverse();
}

function sortByPos1(a: any, b: any) {
  return a.pos - b.pos;
}

function binarySearch(arr: any[], item: any, comparator: (a: any, b: any) => number): number {
  let low = 0;
  let high = arr.length;
  let mid: number;
  while (low < high) {
    mid = (low + high) >>> 1;
    if (comparator(arr[mid], item) < 0) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }
  return low;
}

function insertSorted(arr: any[], item: any, comparator: (a: any, b: any) => number) {
  const idx = binarySearch(arr, item, comparator);
  arr.splice(idx, 0, item);
}

function pathToTree(path: any[], numStemmed: number): any {
  let root: any;
  let leaf: any;
  for (let i = numStemmed, len = path.length; i < len; i++) {
    const node = path[i];
    const currentLeaf = [node.id, node.opts, []];
    if (leaf) {
      leaf[2].push(currentLeaf);
      leaf = currentLeaf;
    } else {
      root = leaf = currentLeaf;
    }
  }
  return root;
}

function compareTree(a: any, b: any): number {
  return a[0] < b[0] ? -1 : 1;
}

function mergeTree(in_tree1: any, in_tree2: any): { conflicts: string | false, tree: any } {
  const queue = [{ tree1: in_tree1, tree2: in_tree2 }];
  let conflicts: string | false = false;
  while (queue.length > 0) {
    const item = queue.pop()!;
    const tree1 = item.tree1;
    const tree2 = item.tree2;
    if (tree1[1].status || tree2[1].status) {
      tree1[1].status =
        (tree1[1].status === 'available' ||
        tree2[1].status === 'available') ? 'available' : 'missing';
    }
    for (let i = 0; i < tree2[2].length; i++) {
      if (!tree1[2][0]) {
        conflicts = 'new_leaf';
        tree1[2][0] = tree2[2][i];
        continue;
      }
      let merged = false;
      for (let j = 0; j < tree1[2].length; j++) {
        if (tree1[2][j][0] === tree2[2][i][0]) {
          queue.push({ tree1: tree1[2][j], tree2: tree2[2][i] });
          merged = true;
        }
      }
      if (!merged) {
        conflicts = 'new_branch';
        insertSorted(tree1[2], tree2[2][i], compareTree);
      }
    }
  }
  return { conflicts, tree: in_tree1 };
}

function doMerge(tree: any[], path: any, dontExpand?: boolean): { tree: any[], conflicts: string | false } {
  let restree: any[] = [];
  let conflicts: string | false = false;
  let merged = false;
  let res: any;

  if (!tree.length) {
    return { tree: [path], conflicts: 'new_leaf' };
  }

  for (let i = 0, len = tree.length; i < len; i++) {
    const branch = tree[i];
    if (branch.pos === path.pos && branch.ids[0] === path.ids[0]) {
      res = mergeTree(branch.ids, path.ids);
      restree.push({ pos: branch.pos, ids: res.tree });
      conflicts = conflicts || res.conflicts;
      merged = true;
    } else if (dontExpand !== true) {
      const t1 = branch.pos < path.pos ? branch : path;
      const t2 = branch.pos < path.pos ? path : branch;
      const diff = t2.pos - t1.pos;

      const candidateParents: any[] = [];
      const trees: any[] = [];
      trees.push({ ids: t1.ids, diff, parent: null, parentIdx: null });
      while (trees.length > 0) {
        const item = trees.pop()!;
        if (item.diff === 0) {
          if (item.ids[0] === t2.ids[0]) {
            candidateParents.push(item);
          }
          continue;
        }
        const elements = item.ids[2];
        for (let j = 0, elementsLen = elements.length; j < elementsLen; j++) {
          trees.push({
            ids: elements[j],
            diff: item.diff - 1,
            parent: item.ids,
            parentIdx: j
          });
        }
      }

      const el = candidateParents[0];
      if (!el) {
        restree.push(branch);
      } else {
        res = mergeTree(el.ids, t2.ids);
        el.parent[2][el.parentIdx] = res.tree;
        restree.push({ pos: t1.pos, ids: t1.ids });
        conflicts = conflicts || res.conflicts;
        merged = true;
      }
    } else {
      restree.push(branch);
    }
  }

  if (!merged) {
    restree.push(path);
  }

  restree.sort(sortByPos1);

  return {
    tree: restree,
    conflicts: conflicts || 'internal_node'
  };
}

function stem(tree: any[], depth: number): { tree: any[], revs: string[] } {
  const paths = rootToLeaf(tree);
  let stemmedRevs: Record<string, boolean> | undefined;
  let result: any[] | undefined;

  for (let i = 0, len = paths.length; i < len; i++) {
    const path = paths[i];
    const stemmed = path.ids;
    let node: any;
    if (stemmed.length > depth) {
      if (!stemmedRevs) {
        stemmedRevs = {};
      }
      const numStemmed = stemmed.length - depth;
      node = {
        pos: path.pos + numStemmed,
        ids: pathToTree(stemmed, numStemmed)
      };
      for (let s = 0; s < numStemmed; s++) {
        const rev = (path.pos + s) + '-' + stemmed[s].id;
        stemmedRevs[rev] = true;
      }
    } else {
      node = {
        pos: path.pos,
        ids: pathToTree(stemmed, 0)
      };
    }
    if (result) {
      result = doMerge(result, node, true).tree;
    } else {
      result = [node];
    }
  }

  if (stemmedRevs) {
    traverseRevTree(result!, function (_isLeaf, pos, revHash) {
      delete stemmedRevs![pos + '-' + revHash];
    });
  }

  return {
    tree: result!,
    revs: stemmedRevs ? Object.keys(stemmedRevs) : []
  };
}

export function merge(tree: any[], path: any, depth: number): { tree: any[], stemmedRevs: string[], conflicts: string | false } {
  const newTree = doMerge(tree, path);
  const stemmed = stem(newTree.tree, depth);
  return {
    tree: stemmed.tree,
    stemmedRevs: stemmed.revs,
    conflicts: newTree.conflicts
  };
}

// ─── parseDoc ─────────────────────────────────────────────────────────

function makeRevTreeFromRevisions(revisions: any, opts: any): any[] {
  const pos = revisions.start - revisions.ids.length + 1;
  const revisionIds = revisions.ids;
  let ids = [revisionIds[0], opts, []];
  for (let i = 1, len = revisionIds.length; i < len; i++) {
    ids = [revisionIds[i], { status: 'missing' }, [ids]];
  }
  return [{ pos, ids }];
}

function parseRevisionInfo(revStr: string): { prefix: number, id: string } | null {
  if (!/^\d+-/.test(revStr)) {
    return null;
  }
  const idx = revStr.indexOf('-');
  return {
    prefix: parseInt(revStr.substring(0, idx), 10),
    id: revStr.substring(idx + 1)
  };
}

let revCounter = 0;

function generateRevId(): string {
  // simple unique id for new revisions (non-deterministic mode)
  revCounter++;
  const id = Date.now().toString(36) + revCounter.toString(36) + Math.random().toString(36).substring(2, 8);
  // CouchDB's revid_to_str/2 hex-encodes any 16-byte binary rev hash, assuming it's a raw MD5.
  // Our base-36 IDs can be 15-18 chars; when exactly 16, CouchDB misinterprets them as binary
  // and hex-encodes in _revs_diff responses, causing PouchDB to look up a non-existent rev locally.
  // Pad to 17 chars to avoid the collision. See: apache/couchdb couch_doc.erl revid_to_str/2.
  return id.length === 16 ? id + '0' : id;
}

export function parseDoc(doc: any, newEdits: boolean): { metadata: any, data: any } | null {
  let nRevNum: number;
  let newRevId: string;
  let revInfo: any;
  const opts: any = { status: 'available' };
  if (doc._deleted) {
    opts.deleted = true;
  }

  if (newEdits) {
    if (!doc._id) {
      doc._id = generateRevId();
    }
    newRevId = generateRevId();
    if (doc._rev) {
      revInfo = parseRevisionInfo(doc._rev);
      if (!revInfo) {
        return null; // invalid rev
      }
      doc._rev_tree = [{
        pos: revInfo.prefix,
        ids: [revInfo.id, { status: 'missing' }, [[newRevId, opts, []]]]
      }];
      nRevNum = revInfo.prefix + 1;
    } else {
      doc._rev_tree = [{
        pos: 1,
        ids: [newRevId, opts, []]
      }];
      nRevNum = 1;
    }
  } else {
    if (doc._revisions) {
      doc._rev_tree = makeRevTreeFromRevisions(doc._revisions, opts);
      nRevNum = doc._revisions.start;
      newRevId = doc._revisions.ids[0];
    }
    if (!doc._rev_tree) {
      revInfo = parseRevisionInfo(doc._rev);
      if (!revInfo) {
        return null;
      }
      nRevNum = revInfo.prefix;
      newRevId = revInfo.id;
      doc._rev_tree = [{
        pos: nRevNum,
        ids: [newRevId, opts, []]
      }];
    }
  }

  doc._rev = nRevNum! + '-' + newRevId!;

  const result: any = { metadata: {}, data: {} };
  const dataWords: Record<string, boolean> = {
    _attachments: true,
    _access: true,
    _replication_id: true,
    _replication_state: true,
    _replication_state_time: true,
    _replication_state_reason: true,
    _replication_stats: true
  };

  for (const key in doc) {
    if (Object.prototype.hasOwnProperty.call(doc, key)) {
      const specialKey = key[0] === '_';
      if (specialKey && !dataWords[key]) {
        result.metadata[key.slice(1)] = doc[key];
      } else {
        result.data[key] = doc[key];
      }
    }
  }
  return result;
}
